import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Badge, Button, Form } from "react-bootstrap";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule, themeQuartz } from "ag-grid-community";
import { abbreviateAmount } from "../../data/formatting";

const myTheme = themeQuartz.withParams({
  backgroundColor: '#212529',
  foregroundColor: '#ffffff',
  borderColor: '#495057',
  headerBackgroundColor: '#343a40',
  accentColor: '#0d6efd',
  browserColorScheme: 'dark',
});

ModuleRegistry.registerModules([AllCommunityModule]);

const SelectFloatingFilter = (props) => {
  const fieldKey = props?.colDef?.field || props?.column?.getColId?.();
  const values = Array.isArray(props?.context?.dropdownOptions?.[fieldKey])
    ? props.context.dropdownOptions[fieldKey]
    : [];
  const [value, setValue] = useState("");

  useEffect(() => {
    if (!props.currentParentModel) {
      setValue("");
    } else {
      setValue(props.currentParentModel.filter || "");
    }
  }, [props.currentParentModel]);

  return (
    <select
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        setValue(next);
        const applyFilter = (instance) => {
          if (!instance || typeof instance.onFloatingFilterChanged !== "function") return;
          if (!next) {
            if (props.api && props.column) {
              props.api.setColumnFilterModel(props.column.getColId(), null);
              props.api.onFilterChanged();
            } else if (typeof instance.setModel === "function") {
              instance.setModel(null);
              if (typeof instance.onFilterChanged === "function") {
                instance.onFilterChanged();
              }
            }
            instance.onFloatingFilterChanged(null, null);
          } else {
            instance.onFloatingFilterChanged("equals", next);
          }
        };

        if (typeof props.parentFilterInstance === "function") {
          props.parentFilterInstance((instance) => applyFilter(instance));
        } else if (typeof props.onFloatingFilterChanged === "function") {
          if (!next) {
            props.onFloatingFilterChanged(null, null);
          } else {
            props.onFloatingFilterChanged("equals", next);
          }
        }
      }}
      style={{
        width: "100%",
        height: "100%",
        minHeight: "28px",
        fontSize: "12px",
        backgroundColor: "#2d2d2d",
        color: "#e0e0e0",
        border: "1px solid #555",
        borderRadius: "4px",
        padding: "2px 6px"
      }}
    >
      <option value="">All</option>
      {values.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </select>
  );
};

const TradesGrid = ({ data, title, onTradeSelect, party, role, onNewTrade, canCreateTrade }) => {
  const [instruments, setInstruments] = useState({});
  const [gridApi, setGridApi] = useState(null);
  const [quickFilter, setQuickFilter] = useState("");
  const filterModelRef = useRef(null);
  const quickFilterRef = useRef("");
  const [highlightNewTrade, setHighlightNewTrade] = useState(true);

  useEffect(() => {
    const loadInstruments = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_TRADES_API}/instruments`);
        if (response.ok) {
          const result = await response.json();
          const instrumentMap = {};
          result.items?.forEach((instrument) => {
            if (instrument?.isin) {
              instrumentMap[instrument.isin] = instrument;
              instrumentMap[`ISIN#${instrument.isin}`] = instrument;
            }
            if (instrument?.id) {
              instrumentMap[instrument.id] = instrument;
            }
          });
          setInstruments(instrumentMap);
        }
      } catch (error) {
        console.error("Error loading instruments:", error);
      }
    };
    loadInstruments();
  }, []);

  const rowData = useMemo(() => {
    if (!data) return [];

    const isTrader = role === "trader";

    return Object.entries(data)
      .filter(([, trade]) => !isTrader || trade.entity === party || trade.cpty === party)
      .map(([key, trade]) => {
        const rawIsin = trade.isin || trade.ISIN || trade.bondIsin || trade.bondISIN;
        const normalizedIsin = typeof rawIsin === "string" && rawIsin.startsWith("ISIN#")
          ? rawIsin.replace("ISIN#", "")
          : rawIsin;
        const instrument = instruments[rawIsin] || instruments[normalizedIsin];
        const bondCcy =
          instrument?.ccy ||
          trade.bondCcy ||
          trade.bondCurrency ||
          trade.bond_ccy ||
          trade.ccy ||
          "N/A";

        const rawType = (trade.tradeType || trade.side || trade.type || "")
          .toString()
          .toLowerCase();
        const baseType = rawType === "offer" ? "sell" : rawType === "bid" ? "buy" : rawType;
        const isCpty = trade.cpty === party;
        const derivedType = !baseType || (baseType !== "buy" && baseType !== "sell")
          ? trade.role === "cpty"
            ? "sell"
            : "buy"
          : baseType;
        const displayType = isCpty ? (derivedType === "buy" ? "sell" : "buy") : derivedType;

        let statusText = "";
        if (trade.status === "confirmed") statusText = "confirmed";
        else if (trade.status === "cancelled") statusText = "cancelled";
        else if (trade.status === "matured") statusText = "matured";
        else if (trade.entity === party) statusText = "sent";
        else if (trade.cpty === party) statusText = "received";

        const tradeDate = moment.utc(Number(trade.timestamp)).isValid()
          ? moment.utc(Number(trade.timestamp)).format("YYYY-MM-DD")
          : "";

        return {
          ...trade,
          key,
          isin: normalizedIsin || trade.isin || trade.ISIN || "",
          bondCcy,
          displayType,
          statusText,
          startDate: trade.start || "",
          endDate: trade.end || "",
          tradeDate,
          tradeTime: moment(Number(trade.timestamp)).format("HH:mm:ss"),
        };
      });
  }, [data, instruments, party, role]);

  const dropdownOptions = useMemo(() => {
    const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));
    return {
      displayType: unique(rowData.map((row) => row.displayType)),
      ccy: unique(rowData.map((row) => row.ccy)),
      entity: unique(rowData.map((row) => row.entity)),
      cpty: unique(rowData.map((row) => row.cpty)),
      statusText: unique(rowData.map((row) => row.statusText))
    };
  }, [rowData]);

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption("context", { dropdownOptions });
    }
  }, [dropdownOptions, gridApi]);

  useEffect(() => {
    const timer = setTimeout(() => setHighlightNewTrade(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!gridApi) return;
    if (filterModelRef.current) {
      gridApi.setFilterModel(filterModelRef.current);
      gridApi.onFilterChanged();
    }
    if (quickFilterRef.current) {
      gridApi.setQuickFilter(quickFilterRef.current);
    }
  }, [rowData, gridApi]);

  const dateFilterComparator = useCallback((filterLocalDateAtMidnight, cellValue) => {
    if (!cellValue) return 0;
    const cellDate = new Date(cellValue);
    if (cellDate < filterLocalDateAtMidnight) return -1;
    if (cellDate > filterLocalDateAtMidnight) return 1;
    return 0;
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Type",
        field: "displayType",
        width: 90,
        filter: "agTextColumnFilter",
        floatingFilterComponent: SelectFloatingFilter,
        cellRenderer: (params) => (
          <Badge bg={params.value === "buy" ? "success" : "danger"}>
            {params.value === "buy" ? "▲ buy" : "▼ sell"}
          </Badge>
        ),
      },
      { headerName: "Trade ID", field: "id", width: 110, filter: "agTextColumnFilter" },
      { headerName: "Product", field: "isin", width: 120, filter: "agTextColumnFilter" },
      { headerName: "UL Ccy", field: "bondCcy", width: 95, filter: "agTextColumnFilter" },
      {
        headerName: "Nominal",
        field: "nominal",
        width: 110,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => abbreviateAmount(params.value),
      },
        { headerName: "Cash Ccy", field: "ccy", width: 95, filter: "agTextColumnFilter", floatingFilterComponent: SelectFloatingFilter },
        { headerName: "Entity", field: "entity", width: 110, filter: "agTextColumnFilter", floatingFilterComponent: SelectFloatingFilter },
      {
        headerName: "Cpty",
        field: "cpty",
        width: 100,
        filter: "agTextColumnFilter",
        floatingFilterComponent: SelectFloatingFilter,
      },
      {
        headerName: "Rate",
        field: "repoRate",
        width: 90,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          const value = Number(params.value);
          return Number.isFinite(value) ? value.toString() : "";
        }
      },
      {
        headerName: "Start",
        field: "startDate",
        width: 130,
        filter: "agDateColumnFilter",
        filterParams: { comparator: dateFilterComparator },
        valueFormatter: (params) => params.data?.startDate || "",
      },
      {
        headerName: "End",
        field: "endDate",
        width: 130,
        filter: "agDateColumnFilter",
        filterParams: { comparator: dateFilterComparator },
        valueFormatter: (params) => params.data?.endDate || "",
      },
      { headerName: "Profit", field: "aip", width: 80, filter: false },
      {
        headerName: "Haircut",
        field: "haircut",
        width: 90,
        filter: false,
        valueFormatter: (params) => {
          const value = Number(params.value);
          return Number.isFinite(value) ? value.toString() : "";
        }
      },
      {
        headerName: "End Cash",
        field: "endCash",
        width: 120,
        filter: false,
        valueFormatter: (params) =>
          new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(params.value),
      },
      {
        headerName: "Trade Date",
        field: "tradeDate",
        width: 135,
        filter: "agDateColumnFilter",
        filterParams: { comparator: dateFilterComparator },
      },
      { headerName: "Trade Time", field: "tradeTime", width: 110, filter: "agTextColumnFilter" },
      {
        headerName: "Status",
        field: "statusText",
        width: 110,
        filter: "agTextColumnFilter",
        floatingFilterComponent: SelectFloatingFilter,
        cellRenderer: (params) => {
          const trade = params.data;
          if (!trade) return null;
          if (trade.status === "confirmed") return <Badge bg="success">confirmed</Badge>;
          if (trade.status === "cancelled") return <Badge bg="danger">cancelled</Badge>;
          if (trade.status === "matured") return <Badge bg="info">matured</Badge>;
          if (trade.entity === party) return <Badge bg="warning">sent</Badge>;
          if (trade.cpty === party) return <Badge bg="primary">received</Badge>;
          return null;
        },
      },
    ],
    [dateFilterComparator, party]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      floatingFilter: true,
      resizable: true,
      minWidth: 80,
    }),
    []
  );

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  const markInteracting = useCallback(() => {
    if (typeof window === "undefined") return;
    window.__gridInteracting = true;
    if (window.__gridInteractTimer) {
      clearTimeout(window.__gridInteractTimer);
    }
    window.__gridInteractTimer = setTimeout(() => {
      window.__gridInteracting = false;
    }, 10000);
  }, []);

  const onRowClicked = useCallback(
    (params) => {
      if (typeof window !== "undefined") {
        window.__lastTrade = params.data;
        console.log("Selected trade:", params.data);
      }
      if (onTradeSelect) onTradeSelect(params.data);
    },
    [onTradeSelect]
  );

  const handleExport = () => {
    if (!gridApi) return;
    gridApi.exportDataAsCsv({
      fileName: `trades_export_${moment().format("YYYY-MM-DD_HHmmss")}.csv`,
    });
  };

  const clearFilters = () => {
    if (!gridApi) return;
    gridApi.setFilterModel(null);
    if (typeof gridApi.setQuickFilter === "function") {
      gridApi.setQuickFilter("");
    } else {
      gridApi.setGridOption("quickFilterText", "");
    }
    setQuickFilter("");
    quickFilterRef.current = "";
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0">{title}</h5>
          {onNewTrade && (
            <Button
              size="sm"
              variant="primary"
              onClick={onNewTrade}
              disabled={canCreateTrade === false}
              className={highlightNewTrade ? "new-trade-wiggle" : ""}
              style={{ minWidth: "160px" }}
            >
              New Trade
            </Button>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Form.Control
            size="sm"
            placeholder="Search..."
            value={quickFilter}
            onChange={(e) => {
              setQuickFilter(e.target.value);
              quickFilterRef.current = e.target.value;
              if (gridApi) {
                if (typeof gridApi.setQuickFilter === "function") {
                  gridApi.setQuickFilter(e.target.value);
                } else {
                  gridApi.setGridOption("quickFilterText", e.target.value);
                }
              }
            }}
            style={{ width: "200px" }}
          />
          <Button size="sm" variant="outline-light" onClick={clearFilters}>
            Clear Filters
          </Button>
          <Button size="sm" variant="success" onClick={handleExport}>
            Export CSV
          </Button>
        </div>
      </div>

      <div style={{ width: "100%", height: "600px" }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          context={{ dropdownOptions }}
          getRowId={(params) => params.data?.id || params.data?.tradeId || params.data?.tradeRef || params.data?.key}
          theme={myTheme}
          animateRows
          onGridReady={onGridReady}
          onFilterChanged={(params) => {
            markInteracting();
            filterModelRef.current = params.api.getFilterModel();
          }}
          onColumnResized={markInteracting}
          onColumnMoved={markInteracting}
          onSortChanged={markInteracting}
          floatingFiltersHeight={32}
          onRowClicked={onRowClicked}
          pagination
          paginationPageSize={25}
          paginationPageSizeSelector={[25, 50, 100]}
        />
      </div>
      <style>{`
        .new-trade-wiggle {
          animation: wiggle 0.6s ease-in-out 4;
        }
        @keyframes wiggle {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
          75% { transform: rotate(-2deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

export default TradesGrid;
